import { useEffect, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL, authFetch } from '@/services/api';

const short = (n: string) => n.replace(/^СЕС\s+/, '');
const levelMeta = (nmae: number | null | undefined) => {
  if (nmae == null) return { label: '—', color: '#9B9BA4' };
  if (nmae <= 5) return { label: 'Відмінно', color: '#16A34A' };
  if (nmae <= 10) return { label: 'Добре', color: '#65A30D' };
  if (nmae <= 20) return { label: 'Задовільно', color: '#D97706' };
  return { label: 'Низько', color: '#D12229' };
};
const reasonLabel: Record<string, string> = {
  curtailment: 'під обмеженням', no_actual: 'без факту', availability: 'аварія інверторів', no_data: 'немає даних',
};

export default function ForecastAccuracyFleetWidget() {
  const { isDark } = useTheme();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/owner/forecast-accuracy?days=7`)
      .then((r) => r.json()).then(setD).catch(() => setD(null)).finally(() => setLoading(false));
  }, []);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';

  const f = d?.fleet;
  const meta = levelMeta(f?.nmaePct);
  const dc = d?.decomposition;

  return (
    <div className="rounded-xl p-4 mb-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-sm font-semibold" style={{ color: textMain }}>Точність прогнозу — портфель</div>
          <div className="text-[11px] mt-0.5" style={{ color: textMuted }}>середньозважений nMAE по «чистих» годинах · 7 днів · виключно незаблоковані СЕС</div>
        </div>
        {f && (
          <div className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${meta.color}1a`, color: meta.color }}>{meta.label}</div>
        )}
      </div>

      {loading ? (
        <div className="text-xs py-4" style={{ color: textMuted }}>Завантаження…</div>
      ) : !f || f.accuracyPct == null ? (
        <div className="text-xs py-4" style={{ color: textMuted }}>Немає «чистих» годин для оцінки (усі СЕС під обмеженням або без факту).</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-center">
          <div className="flex items-end gap-3">
            <div>
              <div className="text-3xl font-extrabold tabular-nums leading-none" style={{ color: meta.color }}>{f.accuracyPct}%</div>
              <div className="text-[11px] mt-1" style={{ color: textMuted }}>nMAE {f.nmaePct}% · зміщення {f.biasPct > 0 ? '+' : ''}{f.biasPct}%</div>
              <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{f.includedAssets} з {f.totalAssets} СЕС · {f.modelHours} год оцінки</div>
            </div>
          </div>
          <div className="rounded-lg p-2.5" style={{ background: innerBg }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: textMuted }}>По станціях</div>
            <div className="space-y-1">
              {(d.assets || []).map((a: any) => (
                <div key={a.name} className="flex items-center justify-between text-[12px]">
                  <span style={{ color: textMain }}>{short(a.name)}</span>
                  <span className="tabular-nums font-semibold" style={{ color: levelMeta(a.nmaePct).color }}>{a.accuracyPct}%<span className="font-normal" style={{ color: textDim }}> · nMAE {a.nmaePct}%</span></span>
                </div>
              ))}
              {(d.excluded || []).length > 0 && (
                <div className="text-[10px] pt-1 mt-1" style={{ color: textDim, borderTop: `1px solid ${cardBorder}` }}>
                  Виключено: {d.excluded.map((e: any) => `${short(e.name)} (${reasonLabel[e.reason] || e.reason})`).join(' · ')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {dc && (
        <div className="text-[10px] mt-3 pt-2" style={{ color: textDim, borderTop: `1px solid ${cardBorder}` }}>
          Години (світловий день): модель {dc.modelHours} · аварія {dc.availabilityHours} · обмеження {dc.curtailmentHours} · без факту {dc.noActualHours}
        </div>
      )}
    </div>
  );
}
